import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AccessApi } from './access-api';
import {
  AttachRequest, AttentionModel, ChangeDetails, ChangeModel, ChangeResult, ClosedMembershipsRequest, CreateNodeRequest, EmployeeModel, ExpectedCountRequest, GrantPreviewModel,
  GrantRequest, GrantRowModel, HistoryQuery, Id, ImpactModel, ImpactRequest, MoveNodeRequest, NodeCardModel, NodeGrantsQuery, Paged, PatchGrantRequest, PatchNodeRequest,
  PeopleQuery, PersonHitModel, PersonRowModel, ReissueGrantRequest, RepeatRow, RightGrantsQuery, RightsModel, SearchResult, TreeModel, UnemployedRow
} from '../models/access.models';

const BASE = '/api/vn/adm/access';

type ParamValue = string | number | boolean | undefined | null;

/** Query parameters are snake_case (`node_id`, `user_id`, `grant_id`); the ones without a value are left out. */
function params(values: Record<string, ParamValue>): HttpParams {
  let result = new HttpParams();
  for (const [name, value] of Object.entries(values)) {
    if (value !== undefined && value !== null && value !== '') {
      result = result.set(name, String(value));
    }
  }
  return result;
}

@Injectable({ providedIn: 'root' })
export class AccessHttpApi implements AccessApi {
  private readonly http = inject(HttpClient);

  getTree(): Promise<TreeModel> {
    return firstValueFrom(this.http.get<TreeModel>(`${BASE}/tree`));
  }

  getNode(id: Id): Promise<NodeCardModel> {
    return firstValueFrom(this.http.get<NodeCardModel>(`${BASE}/nodes/${id}`));
  }

  getNodePeople(id: Id, query: PeopleQuery): Promise<Paged<PersonRowModel>> {
    return firstValueFrom(this.http.get<Paged<PersonRowModel>>(`${BASE}/nodes/${id}/people`, {
      params: params({ scope: query.scope, role: query.role, query: query.query, page: query.page, limit: query.limit })
    }));
  }

  getNodeGrants(id: Id, query: NodeGrantsQuery): Promise<Paged<GrantRowModel>> {
    return firstValueFrom(this.http.get<Paged<GrantRowModel>>(`${BASE}/nodes/${id}/grants`, {
      params: params({ scope: query.scope, key: query.key, page: query.page, limit: query.limit })
    }));
  }

  search(query: string): Promise<SearchResult> {
    return firstValueFrom(this.http.get<SearchResult>(`${BASE}/search`, { params: params({ query }) }));
  }

  createNode(req: CreateNodeRequest): Promise<ChangeResult> {
    return firstValueFrom(this.http.post<ChangeResult>(`${BASE}/nodes`, req));
  }

  patchNode(id: Id, req: PatchNodeRequest): Promise<ChangeResult> {
    return firstValueFrom(this.http.patch<ChangeResult>(`${BASE}/nodes/${id}`, req));
  }

  impact(id: Id, req: ImpactRequest): Promise<ImpactModel> {
    return firstValueFrom(this.http.post<ImpactModel>(`${BASE}/nodes/${id}/impact`, req));
  }

  moveNode(id: Id, req: MoveNodeRequest): Promise<ChangeResult> {
    return firstValueFrom(this.http.post<ChangeResult>(`${BASE}/nodes/${id}/move`, req));
  }

  removeNode(id: Id): Promise<ChangeResult> {
    return firstValueFrom(this.http.delete<ChangeResult>(`${BASE}/nodes/${id}`));
  }

  getRights(): Promise<RightsModel> {
    return firstValueFrom(this.http.get<RightsModel>(`${BASE}/rights`));
  }

  getRightGrants(key: string, query: RightGrantsQuery): Promise<Paged<GrantRowModel>> {
    return firstValueFrom(this.http.get<Paged<GrantRowModel>>(`${BASE}/rights/${encodeURIComponent(key)}/grants`, {
      params: params({ filter: query.filter, query: query.query, page: query.page, limit: query.limit })
    }));
  }

  previewGrant(req: GrantRequest): Promise<GrantPreviewModel> {
    return firstValueFrom(this.http.post<GrantPreviewModel>(`${BASE}/grants/preview`, req));
  }

  createGrant(req: GrantRequest): Promise<ChangeResult> {
    return firstValueFrom(this.http.post<ChangeResult>(`${BASE}/grants`, req));
  }

  patchGrant(id: Id, req: PatchGrantRequest): Promise<ChangeResult> {
    return firstValueFrom(this.http.patch<ChangeResult>(`${BASE}/grants/${id}`, req));
  }

  revokeGrant(id: Id): Promise<ChangeResult> {
    return firstValueFrom(this.http.delete<ChangeResult>(`${BASE}/grants/${id}`));
  }

  reissueGrant(id: Id, req: ReissueGrantRequest): Promise<ChangeResult> {
    return firstValueFrom(this.http.post<ChangeResult>(`${BASE}/grants/${id}/reissue`, req));
  }

  searchPeople(query: string): Promise<PersonHitModel[]> {
    return firstValueFrom(this.http.get<PersonHitModel[]>(`${BASE}/people/search`, { params: params({ query }) }));
  }

  getPerson(userId: Id): Promise<EmployeeModel> {
    return firstValueFrom(this.http.get<EmployeeModel>(`${BASE}/people/${userId}`));
  }

  getAttention(): Promise<AttentionModel> {
    return firstValueFrom(this.http.get<AttentionModel>(`${BASE}/attention`));
  }

  getAttentionUnemployed(page: number, limit: number): Promise<Paged<UnemployedRow>> {
    return firstValueFrom(this.http.get<Paged<UnemployedRow>>(`${BASE}/attention/unemployed`, { params: params({ page, limit }) }));
  }

  getAttentionRepeats(page: number, limit: number): Promise<Paged<RepeatRow>> {
    return firstValueFrom(this.http.get<Paged<RepeatRow>>(`${BASE}/attention/repeats`, { params: params({ page, limit }) }));
  }

  revokeUnemployed(req: ExpectedCountRequest): Promise<ChangeResult> {
    return firstValueFrom(this.http.post<ChangeResult>(`${BASE}/attention/unemployed/revoke`, req));
  }

  removeRepeats(req: ExpectedCountRequest): Promise<ChangeResult> {
    return firstValueFrom(this.http.post<ChangeResult>(`${BASE}/attention/repeats/remove`, req));
  }

  removeClosedMemberships(req: ClosedMembershipsRequest): Promise<ChangeResult> {
    return firstValueFrom(this.http.post<ChangeResult>(`${BASE}/attention/closed-memberships/remove`, req));
  }

  attachOutOfTree(req: AttachRequest): Promise<ChangeResult> {
    return firstValueFrom(this.http.post<ChangeResult>(`${BASE}/attention/out-of-tree/attach`, req));
  }

  removeEmptyGroups(req: { nodeIds: Id[] }): Promise<ChangeResult> {
    return firstValueFrom(this.http.post<ChangeResult>(`${BASE}/attention/empty-groups/remove`, req));
  }

  getHistory(query: HistoryQuery): Promise<Paged<ChangeModel>> {
    return firstValueFrom(this.http.get<Paged<ChangeModel>>(`${BASE}/history`, {
      params: params({ page: query.page, limit: query.limit, kind: query.kind, node_id: query.nodeId, user_id: query.userId, grant_id: query.grantId })
    }));
  }

  getChange(id: Id): Promise<ChangeDetails> {
    return firstValueFrom(this.http.get<ChangeDetails>(`${BASE}/history/${id}`));
  }

  undo(id: Id): Promise<ChangeResult> {
    return firstValueFrom(this.http.post<ChangeResult>(`${BASE}/history/${id}/undo`, {}));
  }
}
